import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react'
import { getSelcomPesaAccount, saveSelcomPesaAccount, deleteSelcomPesaAccount } from '../hooks/hooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert, Box, Button, Snackbar, TextField, Typography } from '@mui/material';

const accountSchema = Yup.object().shape({
  phone_number: Yup.string()
    .required('Mobile number is required')
    .matches(/^0\d{9}$/, 'Enter a valid mobile number, e.g. 0712345678'),
  account_name: Yup.string()
    .max(255, 'Account name must be 255 characters or less'),
});

const inputLabelSx = {
  sx: {
    color: "#555",
    fontSize: 12,
    transition: "color 0.3s ease",
    "&:hover": {
        color: "#333"
    },
    "&.Mui-focused": {
        color: "#212121",
        textTransform:"uppercase",
        fontWeight:500
    },
  },
};

const inputSx = {
  sx: {
    fontSize: 10,
    color: "#333",
    "&::placeholder": {
        color: "#aaa",
        fontSize: 12,
    },
  },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
      borderRadius: "2px",
      "&.Mui-focused fieldset": {
          borderColor: "#212121",
          borderRadius:"2px"
      },
  },
  '& .MuiFormHelperText-root': {
      fontSize: 10,
      color: 'error.main',
      textTransform:"capitalize"
  },
};

const SelcomPesaAccount = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data, isLoading } = useQuery({
    queryKey: ['selcomPesaAccount'],
    queryFn: getSelcomPesaAccount,
  });

  const savedAccount = data?.data || null;

  const formik = useFormik({
    initialValues: {
      phone_number: '',
      account_name: '',
    },
    validationSchema: accountSchema,
    onSubmit: (values) => {
      setLoading(true);
      mutation.mutate(values);
    },
  });

  useEffect(() => {
    if (savedAccount) {
      formik.setValues({
        phone_number: savedAccount.phone_number || '',
        account_name: savedAccount.account_name || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAccount]);

  const mutation = useMutation({
    mutationFn: saveSelcomPesaAccount,
    onSuccess: () => {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ['selcomPesaAccount'] });
      setSnackbar({ open: true, message: 'Your SelcomPesa account has been saved.', severity: 'success' });
    },
    onError: () => {
      setLoading(false);
      setSnackbar({ open: true, message: 'Unable to save your SelcomPesa account. Please try again later.', severity: 'error' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: deleteSelcomPesaAccount,
    onSuccess: () => {
      setRemoving(false);
      queryClient.invalidateQueries({ queryKey: ['selcomPesaAccount'] });
      formik.resetForm();
      setSnackbar({ open: true, message: 'Your SelcomPesa account has been removed.', severity: 'success' });
    },
    onError: () => {
      setRemoving(false);
      setSnackbar({ open: true, message: 'Unable to remove your SelcomPesa account. Please try again later.', severity: 'error' });
    },
  });

  const handleRemove = () => {
    setRemoving(true);
    removeMutation.mutate();
  };

  return (
    <Box sx={{ margin: 2 }}>
      <Box maxWidth={400} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{
            fontSize:12,textTransform:"uppercase",fontWeight: 600,
            color:"#212121",
        }} gutterBottom>
          SelcomPesa Account
        </Typography>
        <Typography sx={{
            fontSize:12,
            fontWeight:400,
            color:"#616161",
            marginBottom:2,
        }} variant="body1">
          Connect your SelcomPesa mobile number to use it for instant withdrawals.
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            size='small'
            margin='normal'
            fullWidth
            label="Mobile Number"
            name="phone_number"
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
            helperText={formik.touched.phone_number && formik.errors.phone_number}
            InputLabelProps={inputLabelSx}
            inputProps={inputSx}
            sx={fieldSx}
            placeholder="e.g. 0712345678"
            disabled={isLoading}
          />
          <TextField
            size='small'
            margin='normal'
            fullWidth
            label="Account Name (optional)"
            name="account_name"
            value={formik.values.account_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.account_name && Boolean(formik.errors.account_name)}
            helperText={formik.touched.account_name && formik.errors.account_name}
            InputLabelProps={inputLabelSx}
            inputProps={inputSx}
            sx={fieldSx}
            placeholder="e.g. My Mobile Money"
            disabled={isLoading}
          />

          <Button variant="contained" fullWidth type="submit"
            disabled={loading || isLoading}
            sx={{
              borderRadius: "3px",
              textTransform:"uppercase",
              fontSize: 12,
              fontWeight:600,
              boxShadow:0,
              backgroundColor:"#D32F2F",
              mt: 2,
              "&:hover": {
                  boxShadow: "none",
              },
            }}>
            {loading ? "Saving..." : savedAccount ? "Update Account" : "Save Account"}
          </Button>

          {savedAccount && (
            <Button variant="outlined" fullWidth type="button"
              onClick={handleRemove}
              disabled={removing || isLoading}
              sx={{
                borderRadius: "3px",
                textTransform:"uppercase",
                fontSize: 12,
                fontWeight:600,
                boxShadow:0,
                mt: 1,
                color: "#D32F2F",
                borderColor: "#D32F2F",
                "&:hover": {
                    boxShadow: "none",
                    borderColor: "#D32F2F",
                },
              }}>
              {removing ? "Removing..." : "Remove Account"}
            </Button>
          )}
        </form>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SelcomPesaAccount
